const BASE = import.meta.env.VITE_API_URL ?? '/api';

type Opts = RequestInit & { auth?: string };

async function request<T>(path: string, opts: Opts = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  if (!(opts.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (opts.auth) headers.set('Authorization', `Bearer ${opts.auth}`);
  const res = await fetch(`${BASE}${path}`, { ...opts, headers });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({ error: 'unknown' }))) as { error?: string; detail?: string };
    const msg = err.error ?? `http_${res.status}`;
    throw new Error(err.detail ? `${msg}: ${err.detail}` : msg);
  }
  return res.json();
}

type Me = {
  id: string;
  email: string;
  displayName: string;
  city: string | null;
  country: string | null;
  avatarUrl: string | null;
  privateMode: boolean;
  sharePublic: boolean;
  shareSlug: string;
  createdAt: number;
};

export const api = {
  // me / settings
  me: (token: string) => request<Me>('/me', { auth: token }),
  updateMe: (
    body: { privateMode?: boolean; displayName?: string; city?: string | null; sharePublic?: boolean },
    token: string,
  ) =>
    request<{ ok: true; updated: number }>('/me', {
      method: 'PATCH', auth: token, body: JSON.stringify(body),
    }),

  // auth
  register: (body: { email: string; password: string; displayName: string; city?: string }) =>
    request<{ token: string; user: { id: string; email: string; displayName: string } }>(
      '/auth/register',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  login: (body: { email: string; password: string }) =>
    request<{ token: string; user: { id: string; email: string; displayName: string } }>(
      '/auth/login',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  google: (idToken: string) =>
    request<{ token: string; user: { id: string; email: string; displayName: string } }>(
      '/auth/google',
      { method: 'POST', body: JSON.stringify({ idToken }) },
    ),

  // catalog
  albums: (token: string) =>
    request<{ albums: any[] }>('/catalog/albums', { auth: token }),
  albumDetail: (slug: string, token: string) =>
    request<{ album: any; pages: any[] }>(`/catalog/albums/${slug}`, { auth: token }),
  pageStickers: (slug: string, pageNumber: number, token: string) =>
    request<{ stickers: any[] }>(`/catalog/albums/${slug}/pages/${pageNumber}`, { auth: token }),

  // collection
  collection: (token: string, album = 'mundial-2026') =>
    request<{
      total: number; have: number; missing: number; duplicates: number;
      completionPct: number; stickers: any[];
    }>(`/collection?album=${album}`, { auth: token }),
  incrementSticker: (stickerId: string, delta: number, token: string) =>
    request<{ ok: true; quantity: number }>('/collection/increment', {
      method: 'POST', auth: token, body: JSON.stringify({ stickerId, delta }),
    }),
  bulkUpdate: (updates: { stickerId: string; quantity: number }[], token: string) =>
    request<{ ok: true; updated: number }>('/collection', {
      method: 'PATCH', auth: token,
      body: JSON.stringify({ updates, source: 'manual' }),
    }),
  setRarity: (stickerId: string, rarityTag: string | null, token: string) =>
    request<{ ok: true; rarityTag: string | null }>('/collection/rarity', {
      method: 'PATCH', auth: token,
      body: JSON.stringify({ stickerId, rarityTag }),
    }),
  missing: (token: string) =>
    request<{ stickers: any[] }>('/collection/missing', { auth: token }),
  duplicates: (token: string) =>
    request<{ stickers: any[] }>('/collection/duplicates', { auth: token }),

  // upload + vision
  upload: (file: File, token: string, albumSlug = 'mundial-2026') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('albumSlug', albumSlug);
    return request<{ uploadId: string; r2_key: string }>('/upload', {
      method: 'POST', body: fd, auth: token,
    });
  },
  analyze: (uploadId: string, token: string, expectedPage?: number) =>
    request<{
      uploadId: string; pageNumber: number | null;
      presentNumbers: number[]; emptySlotNumbers: number[];
      confidence: number; notes?: string;
    }>('/vision/analyze', {
      method: 'POST', auth: token,
      body: JSON.stringify({ uploadId, expectedPage }),
    }),
  confirmVision: (uploadId: string, additions: number[], removals: number[], token: string) =>
    request<{ ok: true; applied: number }>('/vision/confirm', {
      method: 'POST', auth: token,
      body: JSON.stringify({ uploadId, additions, removals }),
    }),
  analyzeLoose: (uploadId: string, token: string) =>
    request<{
      uploadId: string;
      detected: { code: string; team: string | null; player: string | null }[];
      matches: { id: string; code: string; number: number; player_name: string | null; team: string | null }[];
      confidence: number;
    }>('/vision/analyze-loose', {
      method: 'POST', auth: token,
      body: JSON.stringify({ uploadId }),
    }),
  confirmLoose: (uploadId: string, stickerIds: string[], token: string) =>
    request<{ ok: true; applied: number }>('/vision/confirm-loose', {
      method: 'POST', auth: token,
      body: JSON.stringify({ uploadId, stickerIds }),
    }),

  // matches
  matches: (token: string, opts: { album?: string; sameCity?: boolean; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.album) qs.set('album', opts.album);
    if (opts.sameCity) qs.set('sameCity', '1');
    if (opts.limit) qs.set('limit', String(opts.limit));
    return request<{
      matches: Array<{
        user_id: string; display_name: string; city: string | null;
        avatar_url: string | null; score: number;
        iCanGive: number[]; iCanReceive: number[];
      }>;
    }>(`/matches?${qs}`, { auth: token });
  },
  proposeTrade: (
    body: { receiverId: string; offerStickerIds: string[]; requestStickerIds: string[]; message?: string; score?: number },
    token: string,
  ) =>
    request<{ tradeId: string }>('/trades', {
      method: 'POST', auth: token, body: JSON.stringify(body),
    }),
  listTrades: (box: 'inbox' | 'outbox', token: string) =>
    request<{ trades: any[] }>(`/trades?box=${box}`, { auth: token }),
  patchTrade: (id: string, action: 'accept' | 'reject' | 'cancel' | 'complete', token: string) =>
    request<{ ok: true; status: string }>(`/trades/${id}`, {
      method: 'PATCH', auth: token, body: JSON.stringify({ action }),
    }),

  // push notifications
  pushSubscribe: (
    body: { endpoint: string; keys: { p256dh: string; auth: string }; ua?: string },
    token: string,
  ) =>
    request<{ ok: true }>('/push/subscribe', {
      method: 'POST', auth: token, body: JSON.stringify(body),
    }),
  pushUnsubscribe: (endpoint: string, token: string) =>
    request<{ ok: true }>(`/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`, {
      method: 'DELETE', auth: token,
    }),
  pushTest: (token: string) =>
    request<{ sent: number; total: number }>('/push/test', {
      method: 'POST', auth: token,
    }),

  // public share
  publicCollection: (slug: string) =>
    request<{
      user: { displayName: string; city: string | null; country: string | null; avatarUrl: string | null };
      stats: { total: number; have: number; missing: number; duplicates: number; completionPct: number };
      missing: Array<{ id: string; number: number; code: string; rarity: string; pageNumber: number; pageTitle: string | null; playerName: string | null; team: string | null }>;
      duplicates: Array<{ id: string; number: number; code: string; rarity: string; pageNumber: number; pageTitle: string | null; playerName: string | null; team: string | null; extras: number }>;
    }>(`/public/collection/${slug}`),
};
